import { useState, useCallback, useEffect } from 'react';
import { defaultAxiosInstance } from '../../../api/defaultInstance';

/**
 * 키워드 수정 기능을 위한 커스텀 훅
 * @param {Function} onSaveSuccess - 저장 성공 시 호출할 콜백 함수
 * @returns {Object} 키워드 수정 관련 상태 및 함수들
 */
export function useKeywordEdit(onSaveSuccess) {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    adCampaignId: null,
    campaigns: [],
    placeName: '',
    placeId: ''
  });
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showModal) {
        setShowModal(false);
        setChanges([]);
      }
    };

    if (showModal) {
      window.addEventListener('keydown', handleEscKey);
    }

    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [showModal]);

  /**
   * 모달 열기 (행 클릭 시)
   */
  const openModal = useCallback((row) => {
    const allCampaigns = row.campaigns || [];
    setModalData({
      adCampaignId: allCampaigns[0]?.adCampaignId || null,
      campaigns: allCampaigns,
      placeName: row.placeName,
      placeId: row.placeId
    });
    setChanges([]);
    setShowModal(true);
  }, []);

  /**
   * 모달 닫기
   */
  const closeModal = useCallback(() => {
    setShowModal(false);
    setChanges([]);
  }, []);

  /**
   * 키워드 변경 처리
   */
  const handleChange = useCallback((adKeywordId, newKeyword, currentKeyword) => {
    if (!newKeyword || newKeyword.trim() === '') {
      setChanges(prev => prev.filter(change => change.adKeywordId !== adKeywordId));
      return;
    }
    
    setChanges(prev => {
      const existingIndex = prev.findIndex(change => change.adKeywordId === adKeywordId);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], newKeyword: newKeyword.trim() };
        return updated;
      } else {
        return [...prev, {
          adKeywordId: adKeywordId,
          currentKeyword: currentKeyword,
          newKeyword: newKeyword.trim()
        }];
      }
    });
  }, []);

  /**
   * 새 키워드 추가
   */
  const handleAddNew = useCallback((adCampaignId) => {
    setChanges(prev => [...prev, {
      adCampaignId: adCampaignId,
      adKeywordId: null,
      currentKeyword: null,
      newKeyword: ''
    }]);
  }, []);

  /**
   * 새 키워드 입력값 업데이트
   */
  const updateNewKeyword = useCallback((change, newValue) => {
    setChanges(prev => {
      const updated = [...prev];
      const changeIdx = updated.findIndex(c => 
        c.adCampaignId === change.adCampaignId && 
        c.adKeywordId === null && 
        c === change
      );
      if (changeIdx >= 0) {
        updated[changeIdx] = { ...updated[changeIdx], newKeyword: newValue };
      }
      return updated;
    });
  }, []);

  /**
   * 새 키워드 삭제
   */
  const removeNewKeyword = useCallback((change) => {
    setChanges(prev => prev.filter(c => c !== change));
  }, []);

  /**
   * 키워드 저장
   */
  const save = useCallback(async () => {
    if (changes.length === 0) {
      alert('변경된 키워드가 없습니다.');
      return false;
    }
    
    try {
      setLoading(true);
      
      const requestData = {
        placeName: modalData.placeName,
        placeId: modalData.placeId,
        keywords: changes.map(change => {
          let keywordCampaignId = change.adCampaignId || null;
          if (change.adKeywordId !== null) {
            const campaign = modalData.campaigns.find(c => 
              c.keywords && c.keywords.some(k => k.adKeywordId === change.adKeywordId)
            );
            keywordCampaignId = campaign?.adCampaignId || null;
          }
          
          return {
            adKeywordId: change.adKeywordId,
            currentKeyword: change.currentKeyword,
            newKeyword: change.newKeyword,
            adCampaignId: keywordCampaignId
          };
        })
      };
      
      const response = await defaultAxiosInstance.post('/advertisement/admin/keyword', requestData);
      
      if (response.data.status === 200) {
        alert('키워드가 성공적으로 수정/생성되었습니다.');
        setShowModal(false);
        setChanges([]);
        if (onSaveSuccess) {
          await onSaveSuccess();
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('키워드 수정 실패:', error);
      alert(error.message || '키워드 수정에 실패했습니다.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [changes, modalData, onSaveSuccess]);

  /**
   * 키워드 클릭 시 네이버 검색 열기
   */
  const openKeywordSearch = useCallback((keyword) => {
    if (showModal) {
      const searchUrl = `https://m.search.naver.com/search.naver?query=${encodeURIComponent(keyword)}&sm=mtp_hty.top&where=m`;
      window.open(searchUrl, '_blank');
    }
  }, [showModal]);

  /**
   * 특정 캠페인의 새 키워드 변경사항 가져오기
   */
  const getNewKeywordsForCampaign = useCallback((adCampaignId) => {
    return changes.filter(change => 
      change.adCampaignId === adCampaignId && change.adKeywordId === null
    );
  }, [changes]);

  /**
   * 특정 키워드의 변경사항 가져오기
   */
  const getChangeForKeyword = useCallback((adKeywordId) => {
    return changes.find(c => c.adKeywordId === adKeywordId);
  }, [changes]);

  return {
    // 상태
    showModal,
    modalData,
    changes,
    loading,
    
    // 액션
    openModal,
    closeModal,
    handleChange,
    handleAddNew,
    updateNewKeyword,
    removeNewKeyword,
    save,
    openKeywordSearch,
    
    // 유틸
    getNewKeywordsForCampaign,
    getChangeForKeyword,
    hasChanges: changes.length > 0
  };
}
